import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { suppliesApi } from '@/api/suppliesApi';
import { storageLocationsApi } from '@/api/storageLocationsApi';
import { DetailPageLayout } from '@/components/detail/DetailPageLayout';
import { EntityLink } from '@/components/detail/EntityLink';
import { HistoryTable } from '@/components/detail/HistoryTable';
import { formatCurrency } from '@/utils/formatters';
import { normalizeUploadedImageUrl } from '@/utils/imageUrls';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'usage', label: 'Products Using This' },
  { id: 'stock', label: 'Stock & Location' },
];

export function SupplyDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const { data: supply, isLoading, isError } = useQuery({
    queryKey: ['supply', id],
    queryFn: () => suppliesApi.getById(id!),
    enabled: !!id,
  });

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['supply-usage', id],
    queryFn: () => suppliesApi.getUsage(id!),
    enabled: !!id && activeTab === 'usage',
  });

  const { data: storageLocation } = useQuery({
    queryKey: ['storage-location', supply?.storageLocationId],
    queryFn: () => storageLocationsApi.getById(supply!.storageLocationId!),
    enabled: !!supply?.storageLocationId && activeTab === 'stock',
  });

  const setTab = (tabId: string) => setSearchParams({ tab: tabId });

  return (
    <DetailPageLayout
      backTo="/supplies"
      backLabel="Supplies"
      title={supply?.name ?? 'Supply'}
      subtitle={supply ? `${supply.supplyClassDisplay || supply.supplyTypeDisplay} · ${supply.sku || 'No SKU'}` : undefined}
      isLoading={isLoading}
      notFound={isError || (!isLoading && !supply)}
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setTab}
    >
      {supply && activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-5 space-y-3">
            <h3 className="font-semibold text-gray-900 dark:text-warm-cream">Details</h3>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div><dt className="text-gray-500">Class</dt><dd>{supply.supplyClassDisplay || '—'}</dd></div>
              <div><dt className="text-gray-500">Type</dt><dd>{supply.supplyTypeDisplay || '—'}</dd></div>
              <div><dt className="text-gray-500">In stock</dt><dd>{supply.quantityInStock} {supply.unitOfMeasure}</dd></div>
              <div><dt className="text-gray-500">Cost/unit</dt><dd>{formatCurrency(supply.costPerUnit)}</dd></div>
              <div><dt className="text-gray-500">Stock value</dt><dd>{formatCurrency(supply.stockValue)}</dd></div>
              <div><dt className="text-gray-500">Reorder at</dt><dd>{supply.reorderLevel}</dd></div>
              {supply.supplier && (
                <div className="col-span-2"><dt className="text-gray-500">Supplier</dt><dd>{supply.supplier}</dd></div>
              )}
            </dl>
            {supply.description && (
              <div className="pt-2 border-t border-gray-200 dark:border-charcoal-700">
                <dt className="text-xs text-gray-500 mb-1">Description</dt>
                <p className="text-sm text-gray-600 dark:text-warm-sand">{supply.description}</p>
              </div>
            )}
            {supply.notes && (
              <div className="pt-2 border-t border-gray-200 dark:border-charcoal-700">
                <dt className="text-xs text-gray-500 mb-1">Notes</dt>
                <p className="text-sm text-gray-600 dark:text-warm-sand">{supply.notes}</p>
              </div>
            )}
          </div>
          {normalizeUploadedImageUrl(supply.imageUrl) && (
            <div className="card p-5 flex items-center justify-center">
              <img src={normalizeUploadedImageUrl(supply.imageUrl) ?? undefined} alt={supply.name} className="max-h-48 rounded-lg object-contain" />
            </div>
          )}
        </div>
      )}

      {supply && activeTab === 'usage' && (
        <HistoryTable
          rows={usage ?? []}
          isLoading={usageLoading}
          rowKey={(u) => `${u.productId}-${u.variantId ?? 'product'}-${u.usageType}`}
          emptyMessage="This supply is not used in any product BOM yet."
          columns={[
            { key: 'product', header: 'Product', render: (u) => <EntityLink type="product" id={u.productId} label={u.productName} /> },
            { key: 'variant', header: 'Variant', render: (u) => u.variantSku ?? '—' },
            { key: 'type', header: 'Usage', render: (u) => u.usageType },
            { key: 'qty', header: 'Qty', align: 'right', render: (u) => `${u.quantityRequired} ${u.unitOfMeasure}` },
          ]}
        />
      )}

      {supply && activeTab === 'stock' && (
        <div className="card p-5 space-y-4">
          <dl className="grid grid-cols-2 gap-3 text-sm max-w-lg">
            <div><dt className="text-gray-500">Quantity on hand</dt><dd className="font-medium">{supply.quantityInStock} {supply.unitOfMeasure}</dd></div>
            <div><dt className="text-gray-500">Low stock</dt><dd>{supply.isLowStock ? 'Yes' : 'No'}</dd></div>
            <div className="col-span-2">
              <dt className="text-gray-500">Storage location</dt>
              <dd className="mt-1">
                {supply.storageLocationId ? (
                  <EntityLink type="storageLocation" id={supply.storageLocationId} label={storageLocation?.name ?? 'View location'} />
                ) : (
                  'Not assigned'
                )}
              </dd>
            </div>
            {supply.storageNotes && (
              <div className="col-span-2"><dt className="text-gray-500">Storage notes</dt><dd>{supply.storageNotes}</dd></div>
            )}
          </dl>
        </div>
      )}
    </DetailPageLayout>
  );
}
